import React, { useState, useRef, useContext, useEffect } from 'react';
import classes from './AddSheet.module.css';
import ReactDOM from 'react-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../config';
import { generateId, matchCatalogItem, getItemCategories, getDefaultQuantitiesForScreen } from '../../config/helpers';
import { UserContext } from '../../store/user-context';

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const ChevronIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M9 6L15 12L9 18" stroke="#565959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AddSheet = ({ group, activeCategory, onClose, initialMode, screen }) => {
  const [mode, setMode] = useState(initialMode || 'menu');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [gearItems, setGearItems] = useState(['']);
  const categoryRef = useRef();
  const UserCtx = useContext(UserContext);
  const user = auth.currentUser;

  const handleClose = () => {
    setMode('menu');
    setError('');
    setGearItems(['']);
    onClose && onClose();
  };

  const addCategory = async () => {
    const enteredValue = categoryRef.current?.value?.trim();
    if (!enteredValue || working) return;
    const LCCategories = group.categories.map(c => c.toLowerCase());
    if (LCCategories.includes(enteredValue.toLowerCase())) {
      setError('This category already exists'); return;
    }
    setWorking(true);
    try {
      const updatedCategories = [...group.categories, enteredValue];
      // Seed an explicit icon key so this new category renders the same
      // puzzle-piece fallback a name with no match got under the old
      // name-derived lookup — see categoryIcons.js. The user can change it
      // from the manage sheet's icon picker afterwards.
      const updatedIcons = { ...(group.categoryIcons || {}), [enteredValue]: 'puzzle' };
      const groupRef = doc(db, 'groups', group.id);
      await updateDoc(groupRef, { categories: updatedCategories, categoryIcons: updatedIcons });
      handleClose();
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setWorking(false);
  };

  const addGearItems = async () => {
    const validItems = gearItems.map(i => i.trim()).filter(i => i.length > 0);
    if (validItems.length === 0 || working) return;
    const LCItems = group.items.map(item => item.name.toLowerCase());
    const duplicates = validItems.filter(i => LCItems.includes(i.toLowerCase()));
    const newOnes = validItems.filter(i => !LCItems.includes(i.toLowerCase()));
    const cat = activeCategory || group.categories[0];

    setWorking(true);
    try {
      // Re-add category to existing duplicate items
      let updatedItems = group.items.map(item => {
        if (duplicates.map(d => d.toLowerCase()).includes(item.name.toLowerCase())) {
          const cats = getItemCategories(item);
          if (!cats.includes(cat)) {
            return { ...item, categories: [...cats, cat] };
          }
        }
        return item;
      });

      // Create new items for non-duplicates — order them below the current
      // lowest order so they always sort to the top, even across repeated adds.
      const minOrder = Math.min(0, ...group.items.map(item => item.order ?? 0));
      // Carry hire/tip/aka through from the catalog when the typed name
      // matches a known item — same source Root.js's populateItems already
      // spreads at signup time.
      const festival = UserCtx?.festival || group?.festival;
      const defaultItems = UserCtx?.itemInfo;
      const itemInfoList = festival?.items || defaultItems || [];
      const newItems = newOnes.map((name, i) => {
        const match = matchCatalogItem(name, itemInfoList);
        return {
          id: generateId(20),
          name,
          categories: [cat],
          order: minOrder - newOnes.length + i,
          addedManually: true,
          organise: [{ assigned: user?.uid || 'unassigned', method: null, checked: false }],
          ...getDefaultQuantitiesForScreen(screen),
          tip: match?.tip || null,
          hire: match?.hire || false,
          aka: match?.aka || null,
        };
      });
      // Prepend (not append) — Need/Pack render items in array order and
      // don't consult `order`, so physical position is what puts them on top there.
      updatedItems = [...newItems, ...updatedItems];
      const groupRef = doc(db, 'groups', group.id);
      await updateDoc(groupRef, { items: updatedItems });
      handleClose();
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setWorking(false);
  };

  const handleGearChange = (index, value) => {
    const updated = [...gearItems];
    updated[index] = value;
    setGearItems(updated);
  };

  const handleGearKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Add new line if last item has content
      if (gearItems[index].trim().length > 0) {
        const updated = [...gearItems, ''];
        setGearItems(updated);
        // Focus next input after render
        setTimeout(() => {
          const inputs = document.querySelectorAll('.' + classes.gearInput);
          if (inputs[index + 1]) inputs[index + 1].focus();
        }, 50);
      }
    }
    if (e.key === 'Backspace' && gearItems[index] === '' && index > 0) {
      // Remove empty line on backspace
      const updated = gearItems.filter((_, i) => i !== index);
      setGearItems(updated);
      setTimeout(() => {
        const inputs = document.querySelectorAll('.' + classes.gearInput);
        if (inputs[index - 1]) inputs[index - 1].focus();
      }, 50);
    }
  };

  const handleSave = () => {
    setError('');
    if (mode === 'category') addCategory();
    else if (mode === 'gear') addGearItems();
  };

  const validCount = gearItems.filter(i => i.trim()).length;

  return ReactDOM.createPortal(
    <>
      <div className={classes.backdrop} onClick={handleClose} />
      <div className={classes.sheet} onClick={(e) => e.stopPropagation()}>

        <div className={classes.sheetHeader}>
          <button className={classes.closeBtn} onClick={handleClose}>
            <CloseIcon />
          </button>
          <span className={classes.sheetTitle}>
            {mode === 'menu' ? 'Add' : mode === 'category' ? 'Add category' : `Add ${activeCategory || 'gear'}`}
          </span>
        </div>

        {mode === 'menu' && (
          <div className={classes.menuItems}>
            <button className={classes.menuItem} onClick={() => setMode('category')}>
              <span>Add category</span>
              <ChevronIcon />
            </button>
            <button className={classes.menuItem} onClick={() => setMode('gear')}>
              <span>Add {activeCategory || 'gear'}</span>
              <ChevronIcon />
            </button>
          </div>
        )}

        {mode === 'category' && (
          <>
            <div className={classes.scrollBody}>
              <input
                ref={categoryRef}
                className={classes.input}
                type="text"
                placeholder="Enter category name"
                onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                autoFocus
              />
              {error && <p className={classes.error}>{error}</p>}
            </div>
            <div className={classes.sheetFooter}>
              <button className={classes.saveBtn} onClick={handleSave} disabled={working}>
                {working ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}

        {mode === 'gear' && (
          <>
            <div className={classes.gearList}>
              {gearItems.map((item, index) => (
                <input
                  key={index}
                  className={classes.gearInput}
                  type="text"
                  placeholder={index === 0 ? 'Enter gear name' : 'Add another...'}
                  value={item}
                  onChange={(e) => handleGearChange(index, e.target.value)}
                  onKeyDown={(e) => handleGearKeyDown(e, index)}
                  autoFocus={index === gearItems.length - 1}
                />
              ))}
            </div>
            {error && <p className={classes.error}>{error}</p>}
            <div className={classes.sheetFooter}>
              <button className={classes.saveBtn} onClick={handleSave} disabled={working}>
                {working ? 'Saving...' : `Save ${validCount > 1 ? validCount + ' items' : 'item'}`}
              </button>
            </div>
          </>
        )}

      </div>
    </>,
    document.body
  );
};

export default AddSheet;